import { A } from 't-lib-ui/widgets';
import { sanitizePlainText, sanitizeLongText } from 't-lib-ui/connector';
import { Popup } from 't-lib-ui/structure';
import { RestUserFeed } from '../schema-copy/api-schema';
import { formatLastPostTime, formatFrequency, formatCategory, formatFetchInterval, formatPostCount } from '../utils/formatting';
import { CategoryIcon } from './CategoryIcons';
import './FeedTile.css';

interface FeedInfoPopupProps {
  feed: RestUserFeed | null | undefined;
  onClose: () => void;
  closeFocus?: string;
}

const FeedInfoPopup: React.FC<FeedInfoPopupProps> = ({ feed, onClose, closeFocus }) => (
  <Popup
    show={!!feed}
    title="Feed Details"
    onClose={onClose}
    closable={true}
    minWidth="300px"
    closeFocus={closeFocus}
    className="feedInfoPopup"
  >
    {feed && (
      <div className="feedTileInfoPopup">
        <div className="feedTileInfoRow">
          <strong>Feed Title:</strong>
          <span>{feed.title}</span>
        </div>
        {feed.shortTitle && feed.shortTitle != feed.title && (
          <div className="feedTileInfoRow">
            <strong>Short Title:</strong>
            <span>{feed.shortTitle}</span>
          </div>
        )}
        {feed.feedStatus == 'NEWSLETTER' ? (
          // Newsletter feeds store mailto: in url/siteUrl — show the inbox address instead.
          <div className="feedTileInfoRow">
            <strong>Subscription Email:</strong>
            <span>{feed.url.replace(/^mailto:/i, '')}</span>
          </div>
        ) : (
          <>
            <div className="feedTileInfoRow">
              <strong>RSS URL:</strong>
              <A href={feed.url} target="_blank" rel="noopener noreferrer">{feed.url}</A>
            </div>
            <div className="feedTileInfoRow">
              <strong>Site URL:</strong>
              <A href={feed.siteUrl} target="_blank" rel="noopener noreferrer">{feed.siteUrl}</A>
            </div>
          </>
        )}
        <div className="feedTileInfoRow">
          <strong>Status:</strong>
          <span>{feed.feedStatus}</span>
        </div>
        {feed.errorType && feed.feedStatus != 'ACTIVE' && (
          <div className="feedTileInfoRow">
            <strong>Error Type:</strong>
            <span>{feed.errorType}</span>
          </div>
        )}
        <div className="feedTileInfoRow">
          <strong>Post Count:</strong>
          <span>{formatPostCount(feed.postCount)}</span>
        </div>
        {feed.postFrequency && (
          <div className="feedTileInfoRow">
            <strong>Post Frequency:</strong>
            <span>{formatFrequency(feed.postFrequency)}</span>
          </div>
        )}
        {feed.fetchIntervalMins && (
          <div className="feedTileInfoRow">
            <strong>Fetch Interval:</strong>
            <span>{feed.webSubActive
              ? feed.webSubBroken ? 'Push notifications (hub unreliable, polling hourly)' : 'None (push notifications active)'
              : formatFetchInterval(feed.fetchIntervalMins)}</span>
          </div>
        )}
        {feed.lastNewPostPubDate && (
          <div className="feedTileInfoRow">
            <strong>Last Post:</strong>
            <span>{formatLastPostTime(feed.lastNewPostPubDate)}</span>
          </div>
        )}
        {feed.category && (
          <div className="feedTileInfoRow">
            <strong>Category:</strong>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CategoryIcon category={feed.category} size={20} color="var(--gray-350)" />
              {formatCategory(feed.category) || feed.category}
            </span>
          </div>
        )}
        <div className="feedTileInfoRow">
          <strong>Paid/Free:</strong>
          <span>{feed.paid === true ? 'Paid' : feed.paid === false ? 'Free' : 'Unknown'}</span>
        </div>
        {feed.sourceFormat && (
          <div className="feedTileInfoRow">
            <strong>Source Format:</strong>
            <span>{feed.sourceFormat == 'rss' ? 'RSS' : feed.sourceFormat == 'atom' ? 'Atom' : 'JSON'}</span>
          </div>
        )}
        {feed.description && (
          <div className="feedTileInfoRow">
            <strong>Description:</strong>
            <span>{sanitizeLongText(sanitizePlainText(feed.description), 500)}</span>
          </div>
        )}
        {feed.downreadPublicComment && (
          <div className="feedTileInfoRow">
            <strong>Downread Comment:</strong>
            <span>{feed.downreadPublicComment}</span>
          </div>
        )}
        {feed.copyright && (
          <div className="feedTileInfoRow">
            <strong>Copyright:</strong>
            <span>{feed.copyright}</span>
          </div>
        )}
      </div>
    )}
  </Popup>
);

export default FeedInfoPopup;
